import { useState } from 'react'
import { Outlet, useParams, useSearchParams } from 'react-router-dom'

import { Box, Button, Tabs, useToggle } from '@factorialco/deprecated-design-system'
import { PayrollBundleProIncidencePageDocument } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import { useUnsafeMemo } from 'modules/core/hooks/useMemo'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { IncidenceMessageStatsProvider } from 'modules/bookkeepers/hooks/IncidenceMessageStatsContext'
import { IncidenceStatsProvider } from 'modules/bookkeepers/hooks/IncidenceStatsContext'
import { IncidencesProvider } from 'modules/bookkeepers/hooks/IncidencesContext'
import useGetIncidences from 'modules/bookkeepers/hooks/useGetIncidences'
import { useMessageFilterContext } from 'modules/bookkeepers/lib/MessageFilterContext'
import ExportDataButton from 'modules/payroll_bundle/components/PageHeader/PayrollExportButton'
import useBulkIncidenceStatusChange from 'modules/payroll_bundle/hooks/useBulkIncidenceStatusChange'
import { usePayrollContextlessPolicies } from 'modules/payroll_bundle/hooks/usePayrollContextlessPolicies'
import { ElectiveIncidenceStatus } from 'modules/payroll_bundle/lib/Incidences/Status'
import ActionsMenu from 'modules/payroll_bundle/pages/IncidencesPage/components/_ActionsMenu'
import FiltersBar from 'modules/payroll_bundle/pages/IncidencesPage/components/_FiltersBar'
import IncidencesPageBody from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesPageBody'
import IncidencesPageHeader from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesPageHeader'
import IncidencesTable from 'modules/payroll_bundle/pages/IncidencesPage/components/_IncidencesTable'
import NewManualIncidenceModal from 'modules/payroll_bundle/pages/IncidencesPage/components/_ManualIncidenceModal/_New'
import { StatusChangeDialog } from 'modules/payroll_bundle/pages/IncidencesPage/components/_StatusChangeDialog'

export const INCIDENCE_TAB_ID = 'incidences'
const MESSAGE_TAB_ID = 'messages'

const ProIncidencesPage = () => {
  const { canDownloadPayrollData } = usePayrollContextlessPolicies()

  const [searchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState(
    searchParams.get('tab') === MESSAGE_TAB_ID ? MESSAGE_TAB_ID : INCIDENCE_TAB_ID
  )
  const [filterTypeFromDashboard] = useState<string>(searchParams.get('type') ?? '')
  const { id } = useParams()
  const incidenceDetail = <Outlet />
  const openedIncidenceId = String(id)
  const messageFilters = useMessageFilterContext()
  const messageIncidencesTab = selectedTab === MESSAGE_TAB_ID

  const {
    filters: mFilters,
    debouncedSearch: mDebouncedSearch,
    filterDate: mFilterDate,
    onFilterChange,
  } = messageFilters

  const { loading, incidences, refetch } = useGetIncidences({
    messageIncidencesOnly: messageIncidencesTab,
    filterTypeFromDashboard,
  })

  const [selectedIncidencesId, setSelectedIncidencesId] = useState<Set<string>>(new Set())
  const { bulkChangeIncidenceStatuses, updatingStatusBulk } = useBulkIncidenceStatusChange({
    onStatusChanged: () => {
      setSelectedIncidencesId(new Set())
    },
  })

  const terminalStates: ElectiveIncidenceStatus[] = ['discarded', 'to-do']

  const confirmStatusChange = (newStatus: ElectiveIncidenceStatus) => {
    if (terminalStates.includes(newStatus)) {
      setConfirmChangeToStatus(newStatus)
    }
  }

  useEffect(() => {
    onFilterChange('name', filterTypeFromDashboard)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const legalEntityIds = mFilters.legal_entity_id ? [String(mFilters.legal_entity_id)] : undefined
  const { data: incidenceStatsData } = useQuery(PayrollBundleProIncidencePageDocument, {
    variables: {
      type: mFilters.name?.split(','),
      status: mFilters.status?.split(','),
      startsOn: mFilterDate.firstDate,
      endsOn: mFilterDate.lastDate,
      search: mDebouncedSearch,
      legalEntitiesIds: legalEntityIds,
      messageFrom: 'bookkeeper',
    },
    fetchPolicy: 'network-only',
  })
  const incidenceStats =
    incidenceStatsData?.bookkeepersManagement.incidenceStatsConnection.nodes ?? []
  const messageStats =
    incidenceStatsData?.bookkeepersManagement.incidenceMessageStatsConnection.nodes ?? []

  const [open, toggleModal] = useToggle(false)
  const handleClose = async () => {
    await Promise.all([refetch()])
    const closeModal = toggleModal(false)
    closeModal()
  }

  const [confirmChangeToStatus, setConfirmChangeToStatus] =
    useState<ElectiveIncidenceStatus | null>(null)
  const selectedIncidences = useUnsafeMemo(() => {
    return incidences.filter((incidence) => selectedIncidencesId.has(incidence.id))
  }, [incidences, selectedIncidencesId])

  const dismissStatusChangeDialog = () => setConfirmChangeToStatus(null)
  const onTabClicked = (tabId: string) => {
    setSelectedTab(tabId)
    if (!loading) {
      if (id === INCIDENCE_TAB_ID) {
        track(trackingEvents.payroll.goToIncidencesTab)
      } else {
        track(trackingEvents.payroll.goToIncidencesMessagesTab)
      }
    }
  }

  const incidenceTable = (
    <Box position="relative">
      {open && <NewManualIncidenceModal onClose={handleClose} hasProfessionals />}
      <Tabs currentId={selectedTab} onTabClicked={(tabId) => onTabClicked(tabId)}>
        <Tabs.Tab id={INCIDENCE_TAB_ID} label={i18n.t('incidences.tabs.incidences')}>
          <Box
            position="absolute"
            style={{
              right: '0',
              top: '0',
            }}
          >
            {confirmChangeToStatus && (
              <StatusChangeDialog
                incidences={selectedIncidences}
                newStatus={confirmChangeToStatus}
                onDismiss={dismissStatusChangeDialog}
                onCommit={(confirmedIncidenceIds, ignoreWarnings) => {
                  const confirmedIncidences = selectedIncidences.filter((i) =>
                    confirmedIncidenceIds.has(i.id)
                  )
                  return bulkChangeIncidenceStatuses(
                    confirmedIncidences,
                    confirmChangeToStatus,
                    ignoreWarnings
                  )
                }}
              />
            )}
          </Box>
          <Box flexDirection="row" gap="s8" flexGrow alignItems="center" width="full">
            <FiltersBar messageTab={false} collapsibleSearchBar />
            <ActionsMenu
              onSelectNewStatus={confirmStatusChange}
              isLoading={updatingStatusBulk}
              selectedElements={selectedIncidences}
            />
            {canDownloadPayrollData && <ExportDataButton />}
            <Button iconName="add" type="secondary" onClick={toggleModal(true)}></Button>
          </Box>
          <IncidencesTable
            incidences={incidences}
            isLoading={loading}
            isMessageTab={false}
            selectedIncidencesId={selectedIncidencesId}
            setSelectedIncidencesId={setSelectedIncidencesId}
          />
        </Tabs.Tab>
        <Tabs.Tab id={MESSAGE_TAB_ID} label={i18n.t('incidences.tabs.messages')}>
          <FiltersBar messageTab collapsibleSearchBar={false} />
          <IncidencesTable
            incidences={incidences}
            isLoading={loading}
            isMessageTab
            selectedIncidencesId={selectedIncidencesId}
            setSelectedIncidencesId={setSelectedIncidencesId}
          />
        </Tabs.Tab>
      </Tabs>
    </Box>
  )

  return (
    <IncidencesProvider value={incidences}>
      <IncidenceStatsProvider value={incidenceStats}>
        <IncidenceMessageStatsProvider value={messageStats}>
          <IncidencesPageHeader />
          <IncidencesPageBody
            incidenceDetail={incidenceDetail}
            selectedIncidenceId={openedIncidenceId}
            loading={loading}
            incidencesTable={incidenceTable}
          />
        </IncidenceMessageStatsProvider>
      </IncidenceStatsProvider>
    </IncidencesProvider>
  )
}

export default ProIncidencesPage
